import React, { useContext } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import AddRounded from '@mui/icons-material/AddRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { AuthContext } from 'authentication/AuthContext';

import * as R from 'ramda';
import PropTypes from 'prop-types';

import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import DataDisplayItem from '../../components/DataDisplayItem';
import ParentBatchSummary from './ParentBatchSummary';
import ShipmentWizardLayout from './ShipmentWizardLayout';
import StatusHeader from './StatusHeader';
import { useCreateShipmentsMutation } from '../../batchesEndpoints';

const infoContainerStyle = {
  columnGap: 3,
  display: 'grid',
  gridTemplateColumns: { lg: '1fr 1fr' },
  rowGap: { lg: 3, xs: 2 },
};

const Summary = ({
  availableDate,
  dataCompanyId,
  onClose,
  onComplete,
  onPrev,
  onReset,
  shipmentState,
  totalProposedVolume,
}) => {
  const navigate = useNavigate();
  const { batchId, shipments } = shipmentState;
  const [createShipments, { isSuccess, isError, error }] = useCreateShipmentsMutation();

  const { affiliates, currentAffiliate } = useContext(AuthContext);
  const dataCompanies = affiliates.reduce((a, c) => {
    return [c.dataCompanyId, ...a];
  }, []);

  if (!dataCompanies.includes(dataCompanyId)) {
    navigate(`/affiliate/${currentAffiliate.customerIdName}/${currentAffiliate.dataCompanyId}`);
  }

  const handleCreateShipment = () => {
    createShipments({
      body: {
        batchId,
        shipments: shipments.map(shipment => ({
          agreementCode: R.prop('agreementCode', shipment),
          bookingOriginId: R.path(['bookingOrigin', 'bookingOriginId'], shipment),
          comment: R.prop('comments', shipment),
          locationId: R.path(['location', 'id'], shipment),
          shipper: {
            companyCustomerIdName: R.path(['shipper', 'shipperIdName'], shipment),
            dataCompanyId,
          },
          volume: parseInt(R.prop('volume', shipment).replace(/[^0-9]/g, '')),
        })),
      },
    });
  };

  const buttonsReview = (
    <>
      <Button onClick={onPrev} sx={{ width: 104 }} variant="outlined">
        Back
      </Button>
      <Button onClick={handleCreateShipment} sx={{ width: 104 }}>
        Submit
      </Button>
    </>
  );
  const buttonsSubmit = (
    <>
      <Button
        onClick={onReset}
        startIcon={<AddRounded sx={{ color: 'secondary.main', mt: -0.5 }} />}
        variant="outlined"
      >
        New Shipment
      </Button>
      <Button onClick={onComplete}>Close</Button>
    </>
  );
  const buttonsError = (
    <>
      <Button onClick={onPrev} sx={{ width: 104 }} variant="outlined">
        Back
      </Button>
      <Button onClick={onComplete}>Close</Button>
    </>
  );

  return (
    <ShipmentWizardLayout
      buttons={isError ? buttonsError : isSuccess ? buttonsSubmit : buttonsReview}
      onClose={R.or(isError, isSuccess) ? onComplete : onClose}
      pageTitle="Add New Shipment"
      parentDetails={<ParentBatchSummary totalProposedVolume={totalProposedVolume} />}
    >
      {R.or(isSuccess, isError) && <StatusHeader error={error?.data?.title} />}
      <Card
        elevation={1}
        sx={{
          p: { md: 3, xs: 2 },
        }}
      >
        <CardHeader sx={{ padding: 0 }} title="Summary" />
        {shipments.map(
          ({ bookingOrigin, comments, location, shipper, agreementCode, volume }, index) => {
            const count = R.add(index, 1);

            return (
              <CardContent
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                sx={{
                  '&:last-child': {
                    paddingBottom: 0,
                  },
                  padding: 0,
                }}
              >
                <Stack spacing={2} sx={{ py: { md: 3, xs: 2 } }}>
                  <Typography variant="subtitle1">Shipment {count}</Typography>
                  <Box sx={infoContainerStyle}>
                    <DataDisplayItem
                      label="Shipper"
                      value={shipper.shipperName}
                      valueVariant="subtitle2"
                    />
                    <DataDisplayItem
                      label="Requested Date"
                      value={format(new Date(availableDate), 'P')}
                      valueVariant="subtitle2"
                    />
                    <DataDisplayItem
                      label="Booking Origin"
                      value={bookingOrigin.bookingOriginName}
                      valueVariant="subtitle2"
                    />
                    <DataDisplayItem
                      label="Volume"
                      value={`${formatNumberWithoutDecimals(volume)} bbl`}
                      valueVariant="subtitle2"
                    />
                    <DataDisplayItem
                      label="Agreement Code"
                      value={agreementCode}
                      valueVariant="subtitle2"
                    />
                    {!R.defaultTo(true, location.isDefaultLocation) && (
                      <DataDisplayItem
                        label="Distribution Location"
                        value={location.locationName}
                        valueVariant="subtitle2"
                      />
                    )}
                    <DataDisplayItem label="Comments" value={comments} valueVariant="subtitle2" />
                  </Box>
                </Stack>
                {count < shipments.length && <Divider />}
              </CardContent>
            );
          }
        )}
      </Card>
    </ShipmentWizardLayout>
  );
};

Summary.propTypes = {
  availableDate: PropTypes.string.isRequired,
  dataCompanyId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  shipmentState: PropTypes.shape({
    batchId: PropTypes.number,
    shipments: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  totalProposedVolume: PropTypes.number.isRequired,
};

export default Summary;
